// @flow
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';

import RoundedButton from '../../../components/Buttons/RoundedButton';
import CloseButton from '../../../components/CCMode/CloseButton';
import ExpandButton from '../../../components/CCMode/ExpandButton';
import StartConversationButton from '../../../components/CCMode/StartConversationButton';
import TranscriptIcon from '../../../components/Icons/Sidebar/Transcript';
import RatingSelector from '../../../components/Rating/RatingSelector';

import { conversations } from '../../../services/api/ava';
import type { CCMode } from '../../../types/cc-mode';
import { trackConversationRating } from '../../../utils/conversation';

import useStyles from './RatingView.styles';

interface Props {
  conversationStatus: Object;
  createNewConversation: () => Promise<void>;
  fetchAvaProfileMetrics: (updatedMetrics: Object) => void;
  history: Object;
  isRatingOpen: boolean;
  rate?: number;
  rateHover?: number;
  setCCMode: (ccMode: CCMode) => void;
  setRate: (value?: number) => void;
  setRateConversationOpen: (isOpen: boolean) => void;
  setRateHover: (value?: number) => void;
  setShowBubbleAboveCCMode: (shouldShow: boolean) => void;
  updateElectronCaptionMode: (isElectronCaptionMode: boolean) => void;
  user: Object;
}

const RatingView = ({
  conversationStatus,
  createNewConversation,
  fetchAvaProfileMetrics,
  history,
  isRatingOpen,
  rate,
  rateHover,
  setCCMode,
  setRate,
  setRateConversationOpen,
  setRateHover,
  setShowBubbleAboveCCMode,
  updateElectronCaptionMode,
  user,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  // Used to keep track if user's mouse went over the conversation widget. We
  // only show the options when hovering over the widget, and not on the rating
  // container.
  const [showExpandCloseOptions, setShowExpandCloseOptions] = useState(false);

  useEffect(() => {
    return () => {
      setRate(undefined);
      setRateHover(undefined);
    };
  }, []);

  const trackRateEvent = (rating: number) => {
    trackConversationRating({
      fetchAvaProfileMetrics,
      rate: rating,
      status: conversationStatus,
      user,
    });
  };

  const submitRating = (rating: number) => {
    if (!rating) {
      trackRateEvent(0);
    } else {
      conversations.rateConversation({
        avaId: user.avaId,
        roomId: conversationStatus.id,
        rating,
        firebaseAuthUID: user.id,
      });
    }
  };

  const onRateChange = (newRate: number) => {
    // Don't submit rate if user already rated
    if (rate) return;

    setRate(newRate);
    trackRateEvent(newRate);
    setRateConversationOpen(false);
    submitRating(newRate);

    if (user?.features?.['forbid-conversation-saving']) {
      setCCMode('home');
    }
  };

  return (
    <div className={classes.root}>
      <section className={classes.ratingContainer} onMouseEnter={() => setShowBubbleAboveCCMode(true)}>
        <button
          className={classes.closeRatingButton}
          onClick={() => {
            setRateConversationOpen(false);
            setCCMode('home');
          }}
        >
          <CloseIcon />
        </button>
        {isRatingOpen && (
          <>
            <Typography className={classes.ratingText}>{t('ccMode.rating.text')}</Typography>
            <div>
              <RatingSelector
                readonly={!!rate}
                initialRating={rate}
                rating={rateHover || rate || 0}
                onChange={onRateChange}
                onHover={(value: number) => {
                  if (rateHover !== value) {
                    setRateHover(value);
                  }
                }}
              />
            </div>
          </>
        )}
        {!user?.features?.['forbid-conversation-saving'] && (
          <RoundedButton
            className={classes.viewTranscriptsButton}
            onClick={() => {
              updateElectronCaptionMode(false);
              setTimeout(() => history.push('/web/conversations'), 0);
            }}
            disabledRipple
            flat
          >
            <TranscriptIcon height={24} width={24} />
            <span className={classes.viewTranscriptsText}>{t('ccMode.rating.viewTranscripts')}</span>
          </RoundedButton>
        )}
      </section>
      <section className={classes.startConversationContainer} onMouseLeave={() => setShowExpandCloseOptions(false)}>
        <div
          className={classnames(classes.buttonsContainer, {
            [classes.buttonsVisible]: showExpandCloseOptions,
          })}
        >
          <ExpandButton
            onClick={() => {
              if (isRatingOpen) {
                onRateChange(0);
              }
              updateElectronCaptionMode(false);
            }}
          />
          <CloseButton onClick={() => window.electronIPC.sendCCMode('close')} />
        </div>
        <StartConversationButton
          onClick={() => {
            setCCMode('conversation');
            createNewConversation();
          }}
          onMouseEnter={() => {
            setShowExpandCloseOptions(true);
            setShowBubbleAboveCCMode(true);
          }}
        />
      </section>
    </div>
  );
};

export default RatingView;
